/**
 * Analytics Integration (Umami, Google Tag Manager & Facebook Pixel)
 * 
 * Umami tracks page views automatically.
 * GTM is initialized in index.html.
 * Facebook Pixel tracks events client-side (requires consent).
 * Use these helpers for custom event tracking.
 */

declare global {
  interface Window {
    umami?: {
      track: (eventName: string, eventData?: Record<string, any>) => void;
      identify: (sessionData: Record<string, any>) => void;
    };
    dataLayer?: Record<string, any>[];
    fbq?: (method: string, eventName: string, params?: Record<string, any>) => void;
  }
}

// Helper to get cookie value
const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

// Helper to check analytics consent
export const hasAnalyticsConsent = (): boolean => {
  const cookieConsent = getCookie('cookieConsent');
  if (!cookieConsent) return false;
  try {
    const preferences = JSON.parse(cookieConsent);
    return preferences.analytics === true;
  } catch {
    return false;
  }
};

// Initialize Facebook Pixel
export const initFacebookPixel = () => {
  if (!hasAnalyticsConsent()) return;
  
  // Only init if not already processed by Facebook
  // Note: window.fbq is now always defined because it's in index.html
  // We check a custom flag or the queue length to guess if it was initialized, 
  // but simpler is to just call init. Facebook handles duplicate inits gracefully usually,
  // but to be clean we check if we already tracked PageView.

  const pixelId = FB_PIXEL_ID;
  if (!pixelId) {
     if (import.meta.env.DEV) console.warn('Facebook Pixel: Missing VITE_FB_PIXEL_ID');
     return;
  }

  // We assume the base code (fbq definition) is already in index.html
  if (window.fbq) {
      // Check if already initialized to avoid double counting or warnings
      // @ts-ignore
      if (window.fbq.getState && window.fbq.getState().pixels && window.fbq.getState().pixels.length > 0) {
          return; 
      }

      window.fbq('init', pixelId);
      window.fbq('track', 'PageView');
      if (import.meta.env.DEV) console.debug('Facebook Pixel initialized');
  }
};

// Facebook Pixel Integration
const FB_PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID;

const sendFacebookEvent = (eventName: string, originalEventName: string) => {
  if (!FB_PIXEL_ID) {
    if (import.meta.env.DEV) {
      console.warn('Facebook Pixel: Missing VITE_FB_PIXEL_ID');
    }
    return;
  }

  // Check for consent
  if (!hasAnalyticsConsent()) {
    if (import.meta.env.DEV) {
      console.debug('Facebook Pixel: Event skipped due to missing consent:', eventName);
    }
    return;
  }

  // Ensure initialized if not yet done (e.g. page just loaded)
  // But now window.fbq is always there, we just need to make sure 'init' was called.
  // We can just rely on initFacebookPixel being idempotent-ish or check state
  initFacebookPixel();

  // Track event via fbq
  if (typeof window.fbq === 'function') {
    window.fbq('track', eventName, {
      content_name: originalEventName
    });
    
    if (import.meta.env.DEV) {
        console.debug('Facebook Pixel Event Sent:', eventName, { original_event: originalEventName });
    }
  } else {
    if (import.meta.env.DEV) {
        console.warn('Facebook Pixel: fbq is not defined');
    }
  }
};

/**
 * Track a custom event in Umami and GTM
 * @param eventName Name of the event
 * @param eventData Optional data object
 */
export const trackEvent = (eventName: string, eventData?: Record<string, any>) => {
  // Umami tracking
  if (window.umami) {
    try {
      window.umami.track(eventName, eventData);
    } catch (error) {
      console.warn('Umami tracking failed:', error);
    }
  }

  // GTM tracking
  if (window.dataLayer) {
    try {
      window.dataLayer.push({
        event: eventName,
        ...eventData
      });
    } catch (error) {
      console.warn('GTM tracking failed:', error);
    }
  }
  
  // Facebook Pixel Tracking
  // Map internal events to Facebook Standard Event "Lead"
  if (
    eventName === 'Cal_Booking_Successful' || 
    eventName.startsWith('Contact_') || 
    eventName === 'Global_Contact_Button_Click' ||
    eventName === 'Footer_Beratungstermin_Click' || 
    eventName === 'CTA_Beratungstermin_Click'
  ) {
    sendFacebookEvent('Lead', eventName);
  }

  if (!window.umami && !window.dataLayer) {
    console.debug('Analytics not loaded, event skipped:', eventName, eventData);
  }
};

/**
 * Identify a session (optional)
 * @param sessionData Data to identify the session
 */
export const identifySession = (sessionData: Record<string, any>) => {
  // Umami identify
  if (window.umami) {
    try {
      window.umami.identify(sessionData);
    } catch (error) {
      console.warn('Umami identify failed:', error);
    }
  }

  // GTM identify (push user properties)
  if (window.dataLayer) {
    try {
      window.dataLayer.push({
        event: 'identify',
        user_properties: sessionData
      });
    } catch (error) {
      console.warn('GTM identify failed:', error);
    }
  }
};
